import React, { useState } from "react";
import { Copy, Eye, FilePlus2, Pencil, Plus, Power, Save, Send, Trash2, X } from "lucide-react";
import FormPreview from "./FormPreview";
import FormDocumentEditor from "./FormDocumentEditor";
import useAuth from "../../../hooks/useAuth";
import {
  createFormTemplate,
  createFormVersion,
  deleteFormVersion,
  getFormsErrorMessage,
  publishFormVersion,
  updateFormTemplate,
  updateFormVersion,
} from "../../../api/formsApi";

const inputClass = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-100";
const buttonClass = "inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50";
const fieldTypes = ["text", "textarea", "number", "checkbox", "date", "select", "email", "phone"];
const definitionFor = (name, formType) => ({
  format_version: 2,
  header_in_document: true,
  fields: [{ key: "name", label: "Patient name", required: true, patient_editable: true, prefill: "patient.name" }],
  blocks: [{ type: "heading", text: "{{clinic.name}}" }, { type: "heading", text: name }, { type: "field", field_key: "name" }, ...(formType === "consent" ? [{ type: "pricing" }, { type: "agreement" }, { type: "signature" }] : [])],
  separate_agreement: true,
});

function withSeparateAgreement(blocks, separate) {
  const signatureIndex = blocks.findIndex((block) => block.type === "signature");
  if (separate || signatureIndex < 0 || blocks.some((block) => block.type === "agreement")) return blocks;
  return [...blocks.slice(0, signatureIndex), { type: "agreement" }, ...blocks.slice(signatureIndex)];
}

function editState(templateId, version, formType, templateName) {
  const fields = version.definition?.fields || [];
  const blocks = (version.definition?.blocks || []).map((block) => ({ ...block }));
  const ui = version.ui_config || {};
  if (!version.definition?.header_in_document) {
    const header = [];
    header.push({
      type: "heading", text: ui.clinic_name || "{{clinic.name}}",
      ...(ui.clinic_name_runs ? { runs: ui.clinic_name_runs, bold: false } : { bold: true }),
      font_size: 20, color: ui.primary_color || "#096dea",
      alignment: ui.clinic_name_alignment || "left",
    });
    if (ui.heading || templateName) header.push({ type: "heading", text: ui.heading || templateName });
    blocks.unshift(...header);
  }
  if (version.definition?.format_version !== 2) {
    blocks.push({ type: "pricing" });
    fields.forEach((field) => blocks.push({ type: "field", field_key: field.key }));
    if (version.requires_signature || formType === "consent") blocks.push({ type: "signature" });
  }
  return {
    templateId,
    versionId: version.id,
    fields: (version.definition?.fields || []).map((field) => ({
      ...field,
      validation: { ...(field.validation || {}) },
      options: [...(field.options || [])],
    })),
    blocks: withSeparateAgreement(blocks, Boolean(version.definition?.separate_agreement)),
    uiConfig: JSON.stringify(version.ui_config || {}, null, 2),
    requiresSignature: Boolean(version.requires_signature) || formType === "consent",
  };
}

function optionValue(label) {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

export default function FormTemplateManager({ botId, templates, onReload }) {
  const { can } = useAuth();
  const canCreate = can("form_templates.create");
  const canUpdate = can("form_templates.update");
  const canPublish = can("form_templates.publish");
  const canDelete = can("form_templates.delete");
  const [name, setName] = useState("");
  const [formType, setFormType] = useState("consent");
  const [editor, setEditor] = useState(null);
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [preview, setPreview] = useState(null);
  const [expandedVersions, setExpandedVersions] = useState({});

  const previewTemplate = (template, version, currentEditor = null) => {
    try {
      const definition = currentEditor ? { format_version: 2, header_in_document: true, separate_agreement: true, fields: currentEditor.fields, blocks: currentEditor.blocks } : version.definition;
      const patient = { name: "Sample Patient", title: "Master", name_with_title: "Master Sample Patient", gender: "male", relationship_to_guardian: "son", phone: "919800000000", email: "patient@example.test", id: "sample-customer", attributes: {} };
      const values = Object.fromEntries((definition.fields || []).map((field) => [field.key, field.type === "checkbox" ? false : field.type === "number" ? 0 : field.type === "select" ? field.options?.[0]?.value || "" : patient[field.key] || "Sample answer"]));
      const ui = { ...(currentEditor ? JSON.parse(currentEditor.uiConfig) : version.ui_config || {}) };
      const context = { ...values, patient, guardian: { name: "Sample Parent", relationship: "father" }, clinic: { name: ui.clinic_name || "Sample Clinic" }, pricing: { total: "90.00" } };
      const substitute = (placeholder, path) => {
        const value = path.trim().split(".").reduce((current, key) => current?.[key], context);
        return value == null ? `[Sample: ${path.trim()}]` : String(value);
      };
      for (const key of ["consent_acknowledgement", "signature_label"]) {
        if (ui[key]) ui[key] = ui[key].replace(/\{\{\s*([^{}]+)\s*\}\}/g, substitute);
      }
      const blocks = (definition.blocks || []).map((block) => {
        const pattern = /\{\{\s*([^{}]+)\s*\}\}/g;
        const resolved = { ...block, text: (block.text || "").replace(pattern, substitute) };
        if (block.runs) {
          let offset = 0;
          const positioned = block.runs.map((run) => {
            const start = offset;
            offset += run.text.length;
            return { start, end: offset, run };
          });
          const runs = [];
          const appendRange = (start, end) => positioned.forEach((item) => {
            const left = Math.max(start, item.start);
            const right = Math.min(end, item.end);
            if (left < right) runs.push({ ...item.run, text: block.text.slice(left, right) });
          });
          let cursor = 0;
          for (const match of block.text.matchAll(pattern)) {
            appendRange(cursor, match.index);
            const style = positioned.find((item) => item.start <= match.index && match.index < item.end)?.run || {};
            runs.push({ ...style, text: substitute(match[0], match[1]) });
            cursor = match.index + match[0].length;
          }
          appendRange(cursor, block.text.length);
          resolved.runs = runs;
        }
        return resolved;
      });
      const treatmentItems = template.form_type === "consent" ? [{ treatment: "Sample treatment", tooth_number: "16", cost: "100.00", discount: "10.00", discount_percent: "10.00", net_cost: "90.00" }] : [];
      setPreview({ sample: true, template, version, editing: Boolean(currentEditor), content: { definition, blocks, ui_config: ui, template_name: template.name, treatment_items: treatmentItems, treatment_total: "90.00" }, form_data: values, requires_signature: template.form_type === "consent" || (currentEditor ? currentEditor.requiresSignature : version.requires_signature) });
    } catch (err) { setError(getFormsErrorMessage(err, "Could not preview the template.")); }
  };

  const createTemplate = async (event) => {
    event.preventDefault();
    if (!canCreate) return;
    if (!name.trim()) return;
    setWorking(true);
    setError("");
    setNotice("");
    try {
      const template = await createFormTemplate(botId, { name: name.trim(), form_type: formType });
      await onReload();
      const version = await createFormVersion(botId, template.id, {
        definition: definitionFor(name.trim(), formType),
        ui_config: {},
        requires_signature: formType === "consent",
      });
      setName("");
      setEditor(editState(template.id, version, formType, template.name));
      setNotice("Template created with a draft version.");
      await onReload();
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not create the template."));
      await onReload();
    } finally {
      setWorking(false);
    }
  };

  const createDraft = async (template) => {
    if (!canCreate) return;
    setWorking(true);
    setError("");
    setNotice("");
    try {
      const latest = template.versions?.[0];
      const version = await createFormVersion(botId, template.id, latest ? { copy_from_version_id: latest.id } : {
        definition: definitionFor(template.name, template.form_type),
        ui_config: {},
        requires_signature: template.form_type === "consent",
      });
      setEditor(editState(template.id, version, template.form_type, template.name));
      setNotice(`Draft v${version.version} created.`);
      await onReload();
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not create a draft version."));
    } finally {
      setWorking(false);
    }
  };

  const cloneTemplate = async (source) => {
    if (!canCreate) return;
    setWorking(true);
    setError("");
    setNotice("");
    let cloned = null;
    try {
      const latest = [...(source.versions || [])].sort((first, second) => second.version - first.version)[0];
      if (!latest) throw new Error("This template has no version to clone.");
      const suffix = " (copy)";
      cloned = await createFormTemplate(botId, {
        name: `${source.name.slice(0, 200 - suffix.length)}${suffix}`,
        form_type: source.form_type,
      });
      const version = await createFormVersion(botId, cloned.id, {
        definition: latest.definition,
        ui_config: latest.ui_config || {},
        requires_signature: source.form_type === "consent" || Boolean(latest.requires_signature),
      });
      await onReload();
      setEditor(editState(cloned.id, version, source.form_type, source.name));
      setNotice(`Template cloned as "${cloned.name}" with a draft version. The original is unchanged.`);
    } catch (err) {
      const message = getFormsErrorMessage(err, "Could not clone the template.");
      setError(cloned ? `${message} The new template "${cloned.name}" was created. Reload the templates to check its draft before retrying.` : message);
      if (cloned) await onReload();
    } finally {
      setWorking(false);
    }
  };

  const renameTemplate = async (templateId) => {
    if (!canUpdate || !renameValue.trim()) return;
    setWorking(true);
    setError("");
    try {
      await updateFormTemplate(botId, templateId, { name: renameValue.trim() });
      setRenameId(null);
      setNotice("Template name updated.");
      await onReload();
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not update the template."));
    } finally {
      setWorking(false);
    }
  };

  const deleteDraft = async (template, version) => {
    if (!canDelete) return;
    if (!window.confirm(`Delete draft v${version.version} of "${template.name}"? The template and published versions will be retained.`)) return;
    setWorking(true);
    setError("");
    setNotice("");
    try {
      await deleteFormVersion(botId, template.id, version.id);
      if (editor?.templateId === template.id && editor.versionId === version.id) setEditor(null);
      if (preview?.template.id === template.id && preview.version.id === version.id) setPreview(null);
      setNotice(`Draft v${version.version} deleted.`);
      await onReload();
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not delete the draft version."));
    } finally {
      setWorking(false);
    }
  };

  const deactivateTemplate = async (template) => {
    if (!canDelete) return;
    const confirmed = window.confirm(`Deactivate "${template.name}"? All versions will be unavailable for new requests. Existing requests and their history will be retained.`);
    if (!confirmed) return;
    setWorking(true);
    setError("");
    setNotice("");
    try {
      await updateFormTemplate(botId, template.id, { is_active: false });
      if (editor?.templateId === template.id) setEditor(null);
      if (preview?.template.id === template.id) setPreview(null);
      setNotice("Template deactivated. Existing requests and history were retained.");
      await onReload();
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not deactivate the template."));
    } finally {
      setWorking(false);
    }
  };

  const saveVersion = async (publish = false) => {
    if (publish ? !canPublish : !canUpdate) return;
    if (!editor) return;
    setWorking(true);
    setError("");
    setNotice("");
    try {
      const keys = editor.fields.map((field) => field.key);
      if (editor.fields.some((field) => !field.label.trim())) throw new Error("Every field needs a label.");
      if (new Set(keys).size !== keys.length) throw new Error("Form field keys must be unique.");
      for (const field of editor.fields) {
        if (field.type === "select") {
          const values = field.options.map((option) => option.value);
          if (!field.options.length || field.options.some((option) => !option.label.trim() || !option.value) || new Set(values).size !== values.length) {
            throw new Error(`Add choices with distinct labels to ${field.label}.`);
          }
        }
      }
      const definition = { format_version: 2, header_in_document: true, separate_agreement: true, fields: editor.fields, blocks: editor.blocks };
      if (editor.requiresSignature && !editor.blocks.some((block) => block.type === "signature")) {
        throw new Error("Insert the signature placeholder before saving a signature-required form.");
      }
      const ui_config = JSON.parse(editor.uiConfig);
      await updateFormVersion(botId, editor.templateId, editor.versionId, {
        definition,
        ui_config,
        requires_signature: editor.requiresSignature,
      });
      if (publish) await publishFormVersion(botId, editor.templateId, editor.versionId);
      setEditor(null);
      setNotice(publish ? "Form version published." : "Draft version saved.");
      await onReload();
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not save this form version."));
    } finally {
      setWorking(false);
    }
  };

  return (
    <section className="border-y border-slate-200 py-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-900">Templates</h2>
          <p className="mt-1 text-sm text-slate-600">Create templates and publish immutable versions.</p>
        </div>
      </div>

      {error && <div role="alert" className="mb-4 border-l-4 border-rose-600 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div>}
      {notice && <div role="status" className="mb-4 border-l-4 border-emerald-600 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{notice}</div>}

      {canCreate && <form onSubmit={createTemplate} className="grid gap-3 border-b border-slate-200 pb-5 sm:grid-cols-[minmax(0,1fr)_180px_auto] sm:items-end">
        <label className="text-sm font-semibold text-slate-700">
          Template name
          <input className={`${inputClass} mt-1`} value={name} onChange={(event) => setName(event.target.value)} maxLength={200} required />
        </label>
        <label className="text-sm font-semibold text-slate-700">
          Type
          <select className={`${inputClass} mt-1`} value={formType} onChange={(event) => setFormType(event.target.value)}>
            <option value="consent">Consent</option>
            <option value="registration">Registration</option>
          </select>
        </label>
        <button type="submit" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-cyan-800 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-900 disabled:opacity-50" disabled={working || !name.trim()}>
          <Plus size={16} /> Create template
        </button>
      </form>}

      <div className="grid items-start gap-4 lg:grid-cols-2" aria-label="Template cards">
        {templates.length === 0 ? <p className="py-5 text-sm text-slate-500 lg:col-span-2">No templates yet.</p> : templates.map((template) => (
          <article key={template.id} aria-label={`${template.name} template`} className={editor?.templateId === template.id ? "min-w-0 border-y border-slate-200 py-4 lg:col-span-2" : "min-w-0 rounded-lg border border-slate-200 bg-white p-4"}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                {renameId === template.id ? <>
                  <input aria-label="Template name" className={`${inputClass} max-w-64`} value={renameValue} onChange={(event) => setRenameValue(event.target.value)} maxLength={200} />
                  <button type="button" className={buttonClass} disabled={working || !renameValue.trim()} onClick={() => renameTemplate(template.id)}>Save name</button>
                  <button type="button" className={buttonClass} onClick={() => setRenameId(null)} aria-label="Cancel rename"><X size={15} /></button>
                </> : <>
                  <h3 className="break-words font-semibold text-slate-900">{template.name}</h3>
                  <span className="border border-slate-300 px-2 py-0.5 text-xs capitalize text-slate-600">{template.form_type}</span>
                  {canUpdate && <button type="button" className="rounded p-1 text-slate-500 hover:bg-slate-100" title="Rename template" aria-label={`Rename ${template.name}`} onClick={() => { setRenameId(template.id); setRenameValue(template.name); }}><Pencil size={14} /></button>}
                </>}
              </div>
              <div className="flex items-center gap-2">
                {canCreate && <button type="button" className={buttonClass} aria-label={`Clone ${template.name}`} title="Clone the latest saved version into a new draft template" disabled={working || !template.versions?.length} onClick={() => cloneTemplate(template)}>
                  <Copy size={15} /> Clone
                </button>}
                {canCreate && <button type="button" className={buttonClass} disabled={working} onClick={() => createDraft(template)}>
                  <FilePlus2 size={15} /> New draft
                </button>}
              </div>
            </div>

            <p className="mt-3 text-xs text-slate-500">{(template.versions || []).length} versions · {(template.versions || []).filter((version) => version.status === "published").length} published</p>
            <div className="mt-2 divide-y divide-slate-100 border-t border-slate-200">
              {[...(template.versions || [])].sort((first, second) => second.version - first.version).slice(0, expandedVersions[template.id] ? undefined : 3).map((version) => <div key={version.id} className="flex items-center justify-between gap-2 py-1 text-sm">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="font-medium text-slate-700">v{version.version}</span>
                  <span className={`text-xs font-semibold capitalize ${version.status === "published" ? "text-emerald-700" : "text-amber-700"}`}>{version.status}</span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {version.status === "draft" && canUpdate && <button type="button" aria-label="Edit draft" title={`Edit draft v${version.version}`} className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50" disabled={working} onClick={() => setEditor(editState(template.id, version, template.form_type, template.name))}><Pencil size={14} /></button>}
                  <button type="button" aria-label="Preview" title={`Preview v${version.version}`} className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 text-slate-700 hover:bg-slate-50" onClick={() => previewTemplate(template, version)}><Eye size={14} /></button>
                  {version.status === "draft" && canDelete && <button type="button" aria-label="Delete draft" title={`Delete draft v${version.version}`} className="inline-flex h-8 w-8 items-center justify-center rounded border border-rose-200 text-rose-800 hover:bg-rose-50 disabled:opacity-50" disabled={working} onClick={() => deleteDraft(template, version)}><Trash2 size={14} /></button>}
                  {version.status === "published" && canDelete && <button type="button" aria-label="Deactivate template" title={`Deactivate ${template.name} (all versions)`} className="inline-flex h-8 w-8 items-center justify-center rounded border border-amber-200 text-amber-800 hover:bg-amber-50 disabled:opacity-50" disabled={working || template.is_active === false} onClick={() => deactivateTemplate(template)}><Power size={14} /></button>}
                </div>
              </div>)}
              {(template.versions || []).length > 3 && <button type="button" aria-expanded={Boolean(expandedVersions[template.id])} className="py-2 text-xs font-semibold text-cyan-800 hover:underline" onClick={() => setExpandedVersions((current) => ({ ...current, [template.id]: !current[template.id] }))}>{expandedVersions[template.id] ? "Show less" : `Show all (${template.versions.length})`}</button>}
              {editor?.templateId === template.id && <div className="mt-3 space-y-3 border border-slate-200 bg-slate-50 p-4">
                <h4 className="text-sm font-bold text-slate-900">Edit draft v{editor.versionId}</h4>
                <section className="space-y-3" aria-label="Form fields">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div><h5 className="text-sm font-bold text-slate-800">Form fields</h5><p className="text-xs font-normal text-slate-500">Configure questions added with Insert placeholder and choose what the patient can change.</p></div>
                    <button type="button" className={buttonClass} disabled={editor.fields.length >= 100} onClick={() => setEditor((current) => {
                      let number = 1;
                      while (current.fields.some((field) => field.key === `field_${number}`)) number += 1;
                      const key = `field_${number}`;
                      return { ...current, fields: [...current.fields, { key, label: "New question", type: "text", required: false, patient_editable: true, validation: {}, options: [] }], blocks: [...current.blocks, { type: "field", field_key: key }] };
                    })}><Plus size={15} /> Add field</button>
                  </div>
                  {editor.fields.length === 0 && <p className="border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">No fields yet. Add a field to start building the form.</p>}
                  {editor.fields.map((field, index) => <article key={`${field.key}-${index}`} className="space-y-3 border border-slate-200 bg-white p-3 sm:p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-800">Field {index + 1}<span className="ml-2 text-xs font-normal text-slate-400">Key: {field.key}</span></div>
                      <div className="flex items-center gap-1">
                        <button type="button" className="rounded p-2 text-slate-500 hover:bg-rose-50 hover:text-rose-700" aria-label={`Remove field ${index + 1}`} title="Remove field" onClick={() => setEditor((current) => ({ ...current, fields: current.fields.filter((_, fieldIndex) => fieldIndex !== index), blocks: current.blocks.filter((block) => block.field_key !== field.key) }))}><Trash2 size={15} /></button>
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="text-xs font-semibold text-slate-600">Question label
                        <input className={`${inputClass} mt-1`} value={field.label} maxLength={200} placeholder="e.g. Patient name" onChange={(event) => setEditor((current) => ({ ...current, fields: current.fields.map((item, fieldIndex) => fieldIndex === index ? { ...item, label: event.target.value } : item) }))} />
                      </label>
                      <label className="text-xs font-semibold text-slate-600">Answer type
                        <select className={`${inputClass} mt-1`} value={field.type || "text"} onChange={(event) => setEditor((current) => ({ ...current, fields: current.fields.map((item, fieldIndex) => fieldIndex === index ? { ...item, type: event.target.value, customer_lookup: event.target.value === "text" && Boolean(item.customer_lookup), options: event.target.value === "select" ? (item.options.length ? item.options : [{ value: "option_1", label: "" }]) : [] } : item) }))}>
                          {fieldTypes.map((type) => <option key={type} value={type}>{type === "textarea" ? "Long answer" : type.charAt(0).toUpperCase() + type.slice(1)}</option>)}
                        </select>
                      </label>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                      <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={Boolean(field.required)} onChange={(event) => setEditor((current) => ({ ...current, fields: current.fields.map((item, fieldIndex) => fieldIndex === index ? { ...item, required: event.target.checked } : item) }))} className="h-4 w-4 rounded border-slate-300 text-cyan-800 focus:ring-cyan-700" />Required</label>
                      <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={Boolean(field.patient_editable)} onChange={(event) => setEditor((current) => ({ ...current, fields: current.fields.map((item, fieldIndex) => fieldIndex === index ? { ...item, patient_editable: event.target.checked } : item) }))} className="h-4 w-4 rounded border-slate-300 text-cyan-800 focus:ring-cyan-700" />Patient can edit</label>
                    </div>
                    {(field.type || "text") === "text" && <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={Boolean(field.customer_lookup)} onChange={(event) => setEditor((current) => ({ ...current, fields: current.fields.map((item, fieldIndex) => fieldIndex === index ? { ...item, customer_lookup: event.target.checked, ...(event.target.checked ? { prefill: null } : {}) } : item) }))} />
                      Search existing records for this name (parent / guardian)
                    </label>}
                    <label className="block text-xs font-semibold text-slate-600">Prefill from customer (optional)
                      <select disabled={Boolean(field.customer_lookup)} className={`${inputClass} mt-1`} value={field.prefill || ""} onChange={(event) => setEditor((current) => ({ ...current, fields: current.fields.map((item, fieldIndex) => fieldIndex === index ? { ...item, prefill: event.target.value || null } : item) }))}>
                        <option value="">Do not prefill</option><option value="patient.name">Customer name</option><option value="patient.phone">Customer phone</option><option value="patient.email">Customer email</option><option value="patient.id">Customer record ID</option>
                      </select>
                    </label>
                    {field.type === "select" && <div className="space-y-2">
                      <div className="text-xs font-semibold text-slate-600">Choices</div>
                      {field.options.map((option, optionIndex) => <div key={`${index}-${optionIndex}`} className="flex items-center gap-2">
                        <input aria-label={`Choice ${optionIndex + 1} label`} className={inputClass} value={option.label} placeholder={`Choice ${optionIndex + 1}`} onChange={(event) => {
                          const label = event.target.value;
                          setEditor((current) => ({ ...current, fields: current.fields.map((item, fieldIndex) => fieldIndex === index ? { ...item, options: item.options.map((choice, choiceIndex) => choiceIndex === optionIndex ? { ...choice, label, value: optionValue(label) } : choice) } : item) }));
                        }} />
                        <button type="button" className="rounded p-2 text-slate-500 hover:bg-rose-50 hover:text-rose-700" aria-label={`Remove choice ${optionIndex + 1}`} onClick={() => setEditor((current) => ({ ...current, fields: current.fields.map((item, fieldIndex) => fieldIndex === index ? { ...item, options: item.options.filter((_, choiceIndex) => choiceIndex !== optionIndex) } : item) }))}><X size={15} /></button>
                      </div>)}
                      <button type="button" className={buttonClass} onClick={() => setEditor((current) => ({ ...current, fields: current.fields.map((item, fieldIndex) => fieldIndex === index ? { ...item, options: [...item.options, { value: "", label: "" }] } : item) }))}><Plus size={14} /> Add choice</button>
                    </div>}
                    {field.type === "number" ? <div className="grid gap-3 sm:grid-cols-2">
                      {["minimum", "maximum"].map((key) => <label key={key} className="text-xs font-semibold text-slate-600">{key === "minimum" ? "Minimum value" : "Maximum value"}<input type="number" className={`${inputClass} mt-1`} value={field.validation?.[key] ?? ""} onChange={(event) => setEditor((current) => ({ ...current, fields: current.fields.map((item, fieldIndex) => fieldIndex === index ? { ...item, validation: { ...item.validation, [key]: event.target.value === "" ? null : Number(event.target.value) } } : item) }))} /></label>)}
                    </div> : !["checkbox", "select"].includes(field.type) && <div className="grid gap-3 sm:grid-cols-2">
                      {["min_length", "max_length"].map((key) => <label key={key} className="text-xs font-semibold text-slate-600">{key === "min_length" ? "Minimum characters" : "Maximum characters"}<input type="number" min="0" max="10000" className={`${inputClass} mt-1`} value={field.validation?.[key] ?? (key === "max_length" ? 1000 : "")} onChange={(event) => setEditor((current) => ({ ...current, fields: current.fields.map((item, fieldIndex) => fieldIndex === index ? { ...item, validation: { ...item.validation, [key]: event.target.value === "" ? null : Number(event.target.value) } } : item) }))} /></label>)}
                    </div>}
                  </article>)}
                </section>
                <p className="text-sm text-slate-600">Edit the whole document below, including the clinic name and headings. Insert variables, itemized billing, signature and relationship dropdowns at the cursor. Drag sections or use Up / Down to change their order. Pricing is populated when sending, not typed here.</p>
                <FormDocumentEditor key={editor.versionId} blocks={editor.blocks} fields={editor.fields} onChange={(blocks, fields) => setEditor((current) => ({ ...current, blocks, fields, requiresSignature: template.form_type === "consent" || blocks.some((block) => block.type === "signature") }))} />
                {(editor.requiresSignature || editor.blocks.some((block) => block.type === "agreement")) && (() => {
                  let editorUi = null;
                  try { editorUi = JSON.parse(editor.uiConfig); } catch { editorUi = null; }
                  const setUiText = (key, value) => setEditor((current) => {
                    let parsed;
                    try { parsed = JSON.parse(current.uiConfig); } catch { return current; }
                    return { ...current, uiConfig: JSON.stringify({ ...parsed, [key]: value.trim() ? value : null }, null, 2) };
                  });
                  return <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3" disabled={!editorUi}>
                    <legend className="px-1 text-sm font-semibold text-slate-700">Agreement and signature text</legend>
                    {editor.blocks.some((block) => block.type === "agreement") && <label className="block text-sm font-semibold text-slate-700">
                      Agreement checkbox text
                      <textarea className={`${inputClass} mt-1`} rows={2} maxLength={1000} value={editorUi?.consent_acknowledgement || ""} placeholder="I have read and agree to the consent content above." onChange={(event) => setUiText("consent_acknowledgement", event.target.value)} />
                    </label>}
                    {editor.requiresSignature && <label className="block text-sm font-semibold text-slate-700">
                      Signature label
                      <input className={`${inputClass} mt-1`} maxLength={200} value={editorUi?.signature_label || ""} placeholder="Patient / Guardian signature" onChange={(event) => setUiText("signature_label", event.target.value)} />
                    </label>}
                    <p className="text-xs text-slate-500">Leave blank to use the default wording. Placeholders such as {"{{guardian.name}}"}, {"{{guardian.relationship}}"}, {"{{patient.name_with_title}}"} and {"{{patient.relationship_to_guardian}}"} are filled when the form is sent.{!editorUi && " Fix the Advanced branding JSON to edit these fields."}</p>
                  </fieldset>;
                })()}
                <details><summary className="cursor-pointer text-sm font-semibold text-slate-700">Advanced branding</summary><label className="block text-sm font-semibold text-slate-700">
                  Advanced PDF branding (JSON)
                  <textarea className={`${inputClass} mt-1 font-mono text-xs`} rows={4} spellCheck={false} value={editor.uiConfig} onChange={(event) => setEditor((current) => ({ ...current, uiConfig: event.target.value }))} />
                </label></details>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={editor.requiresSignature} disabled={template.form_type === "consent"} onChange={(event) => {
                    const required = event.target.checked;
                    setEditor((current) => ({ ...current, requiresSignature: required, blocks: required ? [...current.blocks.filter((block) => block.type !== "signature"), { type: "signature" }] : current.blocks.filter((block) => block.type !== "signature") }));
                  }} className="h-4 w-4 rounded border-slate-300 text-cyan-800 focus:ring-cyan-700 disabled:opacity-60" />
                  Signature required{template.form_type === "consent" ? " (required for consent)" : ""}
                </label>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={buttonClass} onClick={() => previewTemplate(template, { id: editor.versionId, requires_signature: editor.requiresSignature }, editor)}><Eye size={15} /> Preview changes</button>
                  {canUpdate && <button type="button" className={buttonClass} disabled={working} onClick={() => saveVersion(false)}><Save size={15} /> Save draft</button>}
                  {canPublish && <button type="button" className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50" disabled={working} onClick={() => saveVersion(true)}><Send size={15} /> Save and publish</button>}
                  <button type="button" className={buttonClass} disabled={working} onClick={() => setEditor(null)}>Close</button>
                </div>
              </div>}
            </div>
          </article>
        ))}
      </div>
      {preview && <FormPreview preview={preview} onClose={() => setPreview(null)}>
        {preview.editing || preview.version.status === "draft" ? <button type="button" className={buttonClass} onClick={() => { if (!preview.editing) setEditor(editState(preview.template.id, preview.version, preview.template.form_type, preview.template.name)); setPreview(null); }}>Back to edit</button> : <button type="button" className={buttonClass} disabled={working} onClick={() => { const template = { ...preview.template, versions: [preview.version] }; setPreview(null); createDraft(template); }}>Create new draft</button>}
      </FormPreview>}
    </section>
  );
}